import { generatePath } from 'react-router-dom';
import { routes, isVocDomain } from '../../routes';
import { BASE_PATH, FIGMA_APP_CLIENT_ID } from '../../../../constants';

export const prototypeProviderMatchers = {
  figma:
    /https:\/\/[\w.-]+\.?figma.com\/(design|board|proto)\/([0-9a-zA-Z]{22,128})(?:\/?([^?]+)?(.*))?$/,
  sketch:
    // eslint-disable-next-line max-len
    /https:\/\/(?:www\.)?sketch\.com\/(?:embed\/)?s\/([0-9a-fA-F-]+)\/prototype\/([0-9a-fA-F-]+)(?:\/[a-z]\/([0-9a-fA-F-]+))?/,
  // eslint-disable-next-line max-len
  xd: /https:\/\/(?:xd\.adobe\.com)\/embed|view\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/screen\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}))?(?:\/?([^?]+)?(.*))?$/,
};

export function isPrototypeUrl(url: string, provider: 'figma' | 'sketch' | 'xd'): boolean {
  return !!url.match(prototypeProviderMatchers[provider]);
}

// based on https://www.figma.com/developers/embed#query-parameters-prototypes
interface FigmaQueryParams {
  'embed-host': string; // Required! A unique string that you use to identify the host
  'client-id': string; // The client id of the OAuth app you set up
  'node-id'?: string; // The id of the node to focus on when the embedded prototype loads
  'starting-point-node-id'?: string; // 	The id of the node that starts the prototype flow that you want to embed.
  footer?: 'true' | 'false'; // 	Whether the Figma footer is displayed at the bottom of the embedded file's viewport.
  'viewport-controls'?: 'true' | 'false'; // 	When true, the user can pan and zoom in the embed.
  // When true, the default keyboard shortcuts for navigating prototypes are disabled.
  'disable-default-keyboard-nav'?: 'true' | 'false';
  // Whether to show a left sidebar that lists all the prototype flows in the embed.
  'show-proto-sidebar'?: 'true' | 'false';
  // When true, clickable areas of the prototype are highlighted when the user clicks in the prototype.
  'hotspot-hints'?: 'true' | 'false';
  'device-frame'?: 'true' | 'false'; // 	Whether to show the device frame for the prototype.
  // Determines the scaling mode for the prototype.
  scaling?: 'scale-down' | 'contain' | 'min-zoom' | 'scale-down-width' | 'fit-width' | 'free';
  // Determines the way content is scaled in the prototype.
  'content-scaling'?: 'fixed' | 'contain' | 'min-zoom' | 'scale-down-width' | 'fit-width' | 'free';
}

function getFigmaUrl(url: string, origin: string) {
  const embedFigmaUrl = new URL(url.replace('www.figma.com', 'embed.figma.com'));
  embedFigmaUrl.searchParams.append('embed-host', 'voc-ut');

  const defaultQueryParams: FigmaQueryParams = {
    'embed-host': 'voc-ut',
    footer: 'false',
    'client-id': FIGMA_APP_CLIENT_ID, // required for using embed api
  };

  let param: keyof typeof defaultQueryParams;
  for (param in defaultQueryParams) {
    const value = defaultQueryParams[param];
    if (value && !embedFigmaUrl.searchParams.has(param)) {
      embedFigmaUrl.searchParams.append(param, value);
    }
  }

  const relativeUrl = generatePath(
    routes.PROTOTYPE_RENDER.getPath(`?test_url=${encodeURIComponent(embedFigmaUrl.toString())}`),
  );
  return new URL(`${BASE_PATH}${relativeUrl}`, origin).href;
}

function getSketchUrl(url: string, origin: string) {
  let embedUrl = url;
  if (embedUrl.indexOf('/embed/') === -1) {
    embedUrl = embedUrl.replace('www.sketch.com', 'www.sketch.com/embed');
  }
  const embedSketchUrl = new URL(embedUrl);
  const relativeUrl = generatePath(
    routes.PROTOTYPE_RENDER.getPath(`?test_url=${encodeURIComponent(embedSketchUrl.toString())}`),
  );
  return new URL(`${BASE_PATH}${relativeUrl}`, origin).href;
}

function getAdobeUrl(url: string, origin: string) {
  const relativeUrl = generatePath(
    routes.PROTOTYPE_RENDER.getPath(
      `?test_url=${encodeURIComponent(url.toString().replace('/view/', '/embed/'))}`,
    ),
  );
  return new URL(`${BASE_PATH}${relativeUrl}`, origin).href;
}

export function generateEmbedUrl(url: string, origin = window.location.origin) {
  if (isPrototypeUrl(url, 'xd')) {
    return getAdobeUrl(url, origin);
  }

  if (isPrototypeUrl(url, 'sketch')) {
    return getSketchUrl(url, origin);
  }

  return getFigmaUrl(url, origin);
}

export function isEmbedUrlValid(url: string): boolean {
  try {
    const providersUrl = new URL(url);
    return (
      (isPrototypeUrl(url, 'figma') &&
        providersUrl.hostname.includes('embed.figma.com') &&
        providersUrl.searchParams.has('embed-host') &&
        providersUrl.searchParams.has('client-id')) ||
      (isPrototypeUrl(url, 'sketch') &&
        providersUrl.hostname.includes('sketch.com') &&
        providersUrl.pathname.includes('/embed/')) ||
      (isPrototypeUrl(url, 'xd') &&
        providersUrl.hostname.includes('xd.adobe.com') &&
        providersUrl.pathname.includes('/embed/'))
    );
  } catch (err) {
    return false;
  }
}

export function isPrototypeTestingUrl(url?: string) {
  return !!url?.includes(routes.PROTOTYPE_RENDER.getPath()) && isVocDomain(url);
}
